import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { ArrowLeft, Send, Loader2, Lock, Users, UserPlus, X, Check, Plus } from "lucide-react";
import { messagingApi } from "@/api/messaging";
import { socialApi } from "@/api/social";
import { useSession } from "@/context/SessionContext";

function AddPeople({ excludeIds, onAdd, onClose }) {
  const [friends, setFriends] = useState(null);
  const [sel, setSel] = useState([]);
  useEffect(() => { socialApi.friends().then((r) => setFriends((r.friends || []).filter((f) => !excludeIds.includes(f.id)))).catch(() => setFriends([])); }, []);
  const toggle = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  return (
    <div className="absolute inset-0 z-50 bg-black/70 flex items-end" data-testid="add-people-picker">
      <div className="w-full bg-cream border-t-4 border-charcoal rounded-t-3xl p-5 max-h-[80dvh] overflow-y-auto no-scrollbar">
        <div className="flex items-center gap-2 pb-3 border-b-2 border-charcoal/10 mb-3">
          <UserPlus size={16} className="text-gold" />
          <h3 className="font-serif text-xl text-charcoal flex-1">Add to chat</h3>
          <button onClick={onClose} data-testid="add-close-btn" className="w-8 h-8 grid place-items-center rounded-lg border-2 border-charcoal bg-white shadow-[1.5px_1.5px_0px_#1A1A1A]"><X size={15} /></button>
        </div>
        {friends == null ? (
          <div className="grid place-items-center py-8"><Loader2 size={20} className="text-charcoal/40 animate-spin" /></div>
        ) : friends.length === 0 ? (
          <p className="font-mono text-[11px] text-charcoal/50 py-6 text-center">No other friends to add.</p>
        ) : friends.map((f) => {
          const on = sel.includes(f.id);
          return (
            <button key={f.id} onClick={() => toggle(f.id)} data-testid={`add-friend-${f.id}`}
              className={`w-full mb-2 border-2 border-charcoal rounded-2xl px-3 py-2.5 flex items-center gap-3 shadow-[2px_2px_0px_#1A1A1A] text-left ${on ? "bg-gold" : "bg-white"}`}>
              <span className="w-9 h-9 rounded-full bg-charcoal grid place-items-center text-gold font-mono text-[10px] font-black shrink-0">{(f.name || "?").slice(0, 2).toUpperCase()}</span>
              <span className="font-mono text-[12px] font-bold text-charcoal truncate flex-1">{f.name}</span>
              {on ? <Check size={16} className="text-charcoal" /> : <Plus size={16} className="text-charcoal/40" />}
            </button>
          );
        })}
        {sel.length > 0 && (
          <button onClick={() => onAdd(sel)} data-testid="add-confirm-btn"
            className="sticky bottom-0 w-full mt-1 bg-charcoal text-gold font-mono text-[11px] font-black uppercase tracking-widest py-3 rounded-full border-2 border-gold shadow-[3px_3px_0px_#D4AF37]">
            Add {sel.length} to chat
          </button>
        )}
      </div>
    </div>
  );
}

export default function ThreadView({ threadId, other, onBack, enableGroups = false }) {
  const { user } = useSession();
  const me = user?.user_id;
  const [msgs, setMsgs] = useState(null);
  const [friends, setFriends] = useState(true);
  const [members, setMembers] = useState([]);
  const [isGroup, setIsGroup] = useState(false);
  const [title, setTitle] = useState("");
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const endRef = useRef(null);
  const wrapRef = useRef(null);
  const [kbInset, setKbInset] = useState(0);

  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return undefined;
    const update = () => {
      const el = wrapRef.current;
      if (!el) return;
      const kbTop = vv.offsetTop + vv.height;
      const overlap = el.getBoundingClientRect().bottom - kbTop;
      setKbInset(Math.max(0, Math.round(overlap)));
    };
    update();
    vv.addEventListener("resize", update);
    vv.addEventListener("scroll", update);
    return () => { vv.removeEventListener("resize", update); vv.removeEventListener("scroll", update); };
  }, []);

  const load = async () => {
    try {
      const r = await messagingApi.threadMessages(threadId);
      setMsgs(r.messages); setFriends(r.friends);
      setMembers(r.members || []); setIsGroup(!!r.is_group); setTitle(r.title || "");
    } catch (e) {
      if (msgs == null) setMsgs([]);
    }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 4000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadId]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs?.length, kbInset]);

  const nameOf = useMemo(() => {
    const map = {};
    (members || []).forEach((m) => { map[m.id] = m.name; });
    return (id) => map[id] || "Someone";
  }, [members]);

  const headerName = isGroup
    ? (title || members.filter((m) => m.id !== me).map((m) => m.name).slice(0, 3).join(", ") || "Group chat")
    : (other?.name || "Direct Message");

  const send = async () => {
    const t = text.trim();
    if (!t || sending) return;
    setSending(true); setText("");
    try { await messagingApi.sendMessage(threadId, t); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not send"); setText(t); }
    finally { setSending(false); }
  };

  const addPeople = async (ids) => {
    try {
      await messagingApi.addParticipants(threadId, ids);
      setAddOpen(false);
      toast.success(ids.length > 1 ? "Added to the chat" : "Added to the chat");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add"); }
  };

  const canSend = isGroup || friends;

  return (
    <div ref={wrapRef} className="absolute inset-0 z-30 flex flex-col bg-cream" style={{ paddingBottom: kbInset || undefined }} data-testid="thread-view">
      {addOpen && <AddPeople excludeIds={members.map((m) => m.id)} onAdd={addPeople} onClose={() => setAddOpen(false)} />}
      <header className="shrink-0 px-4 pt-4 pb-3 flex items-center gap-3 border-b-4 border-charcoal bg-white">
        <button onClick={onBack} data-testid="thread-back-btn" aria-label="Back" className="w-10 h-10 rounded-full bg-white border-2 border-charcoal grid place-items-center shadow-[3px_3px_0px_#1A1A1A] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none">
          <ArrowLeft size={18} strokeWidth={2.4} />
        </button>
        <span className="w-10 h-10 rounded-full bg-charcoal border-2 border-charcoal grid place-items-center text-gold font-mono text-xs font-black overflow-hidden">
          {isGroup ? <Users size={17} /> : (headerName || "?").slice(0, 2).toUpperCase()}
        </span>
        <div className="leading-none min-w-0 flex-1">
          <p className="font-mono text-[9px] uppercase tracking-[0.24em] text-charcoal/50">{isGroup ? `Group · ${members.length}` : "Private · 1:1"}</p>
          <h1 className="font-serif text-xl text-charcoal truncate">{headerName}</h1>
        </div>
        {enableGroups && (
          <button onClick={() => setAddOpen(true)} data-testid="thread-add-people-btn" aria-label="Add people"
            className="w-10 h-10 rounded-full bg-gold border-2 border-charcoal grid place-items-center shadow-[3px_3px_0px_#1A1A1A] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none shrink-0">
            <UserPlus size={17} strokeWidth={2.4} className="text-charcoal" />
          </button>
        )}
      </header>

      <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar px-4 py-4 space-y-2" data-testid="thread-messages">
        {msgs == null ? (
          <div className="h-full grid place-items-center"><Loader2 size={20} className="text-charcoal/40 animate-spin" /></div>
        ) : msgs.length === 0 ? (
          <p className="text-center font-mono text-[11px] text-charcoal/40 pt-8">Say hello 👋</p>
        ) : msgs.map((m) => {
          const mine = m.sender_id === me;
          return (
            <div key={m.id} className={`flex flex-col ${mine ? "items-end" : "items-start"}`}>
              {isGroup && !mine && <span className="font-mono text-[8px] uppercase tracking-widest text-charcoal/40 mb-0.5 pl-1">{nameOf(m.sender_id)}</span>}
              <div className={`max-w-[78%] rounded-2xl border-2 border-charcoal px-3 py-2 shadow-[2px_2px_0px_#1A1A1A] ${mine ? "bg-gold text-black" : "bg-white text-charcoal"}`}>
                <p className="font-mono text-[12px] leading-snug break-words">{m.text}</p>
              </div>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>

      {canSend ? (
        <div className="shrink-0 border-t-4 border-charcoal bg-white px-3 py-3 flex items-center gap-2">
          <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
            data-testid="thread-input" placeholder={isGroup ? "Message the group…" : "Send a private message…"}
            className="flex-1 bg-cream border-2 border-charcoal rounded-full px-4 py-2.5 font-mono text-xs text-charcoal outline-none focus:ring-2 focus:ring-gold shadow-[2px_2px_0px_#1A1A1A]" />
          <button onClick={send} disabled={sending} data-testid="thread-send-btn" className="w-11 h-11 rounded-full bg-gold border-2 border-charcoal grid place-items-center shadow-[3px_3px_0px_#1A1A1A] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none disabled:opacity-60">
            {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} className="text-charcoal" />}
          </button>
        </div>
      ) : (
        <div className="shrink-0 border-t-4 border-charcoal bg-red-50 px-4 py-4 flex items-center justify-center gap-2" data-testid="thread-blocked">
          <Lock size={14} className="text-red-600" />
          <span className="font-mono text-[10px] font-black uppercase tracking-widest text-red-600">Messaging Blocked — mutual friendship required</span>
        </div>
      )}
    </div>
  );
}
